import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { useUserLanguage } from 'common/hooks/useUserLanguage';
import { formatDateTimeRelative } from 'common/utils/dates/dateUtils';
import { fetchChangeLogByWarehouseAndItem } from 'features/warehouse/common/api';

import { ItemChangeLogActionType, ItemChangeLogDetails } from './types';
import { InventoryQuantity } from '../InventoryQuantity';

const Root = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${color.border};
`;

const Title = styled(T.subheadline)`
  margin-bottom: 16px;
`;

const ChangeLogRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${color.border};
  }
`;

const LeftColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RightColumn = styled.div`
  white-space: nowrap;
`;

const StyledIcon = styled.div<{ fill?: string }>`
  min-width: 16px;
  height: 16px;
`;

type Props = {
  selectedWarehouseId: string;
  selectedItemId: string;
};

export const ItemChangeLogLegacy: React.FC<Props> = ({ selectedWarehouseId, selectedItemId }) => {
  const formatMessage = useFormatMessage();
  const language = useUserLanguage();
  const isMobile = useMatchMedia('maxMd');

  // TODO: error handling?
  const { data: itemChangeLog } = useQuery({
    queryKey: ['changeLog', selectedWarehouseId, selectedItemId],
    queryFn: () => fetchChangeLogByWarehouseAndItem(selectedWarehouseId || '', selectedItemId),
    enabled: !!selectedWarehouseId,
  });

  const getItemChangeLogDetails = (
    actionType: ItemChangeLogActionType,
    inboundLoadPublicId?: string,
  ): ItemChangeLogDetails | undefined => {
    const details: Partial<Record<ItemChangeLogActionType, ItemChangeLogDetails>> = {
      GOODS_RECEIPT: {
        icon: icons.plusCircleFilled.medium,
        iconColor: color.textPositive,
        messageId: inboundLoadPublicId
          ? 'warehouse.common.change_log_added_with_po'
          : 'warehouse.common.change_log_added',
      },
      SHRINKAGE: {
        icon: icons.minusCircleFilled.medium,
        iconColor: color.textNegative,
        messageId: 'warehouse.common.change_log_removed',
      },
      INVENTORY_ADJUSTMENT: {
        icon: icons.checkmarkCircleFilled.medium,
        iconColor: color.textBrand,
        messageId: 'warehouse.common.change_log_adjusted',
      },
    };
    return details[actionType];
  };

  return itemChangeLog?.items?.length ? (
    <Root>
      <Title>{formatMessage('warehouse.common.changes_in_inventory')}</Title>
      {itemChangeLog.items.map((log) => {
        const {
          actionType,
          createdAt,
          id,
          inboundLoad,
          location,
          newQuantity,
          previousQuantity,
          quantityAdjusted,
        } = log;

        const isInventoryAdjustment = actionType === 'INVENTORY_ADJUSTMENT' ? true : false;
        const details = getItemChangeLogDetails(actionType, inboundLoad?.publicId);
        const { icon, iconColor, messageId } = details || {};
        const quantity = isInventoryAdjustment ? newQuantity?.value : quantityAdjusted.value;
        return (
          !!details && (
            <ChangeLogRow key={id}>
              <LeftColumn>
                <StyledIcon as={icon} fill={iconColor} />
                <span data-test-id="item-inventory-changes">
                  <FormattedMessage
                    id={messageId}
                    values={{
                      quantity,
                      location:
                        location?.name || formatMessage('warehouse.common.unknown_location'),
                      poName: inboundLoad?.publicId,
                      measurementUnit: formatMessage('warehouse.measurement_unit:UNITS', {
                        count: quantity,
                      }),
                    }}
                  />
                  {isInventoryAdjustment && (
                    <>
                      <InventoryQuantity
                        quantity={previousQuantity?.value}
                        measurementUnit={previousQuantity?.measurementUnit}
                        isPreviousQuantity={true}
                      />{' '}
                    </>
                  )}
                </span>
              </LeftColumn>
              <RightColumn>
                {formatDateTimeRelative(language, createdAt, isMobile ? 'short' : 'long')}
              </RightColumn>
            </ChangeLogRow>
          )
        );
      })}
    </Root>
  ) : null;
};
